import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PackageCheck, Truck, CheckCircle2, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "processing", label: "Processing", icon: PackageCheck, desc: "Order received — your parts are being prepped / cut." },
  { key: "shipped", label: "Shipped", icon: Truck, desc: "On the way with tracked worldwide delivery." },
  { key: "delivered", label: "Delivered", icon: CheckCircle2, desc: "Bolt it on and send it." },
];

export default function Track() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/orders/track`, {
        params: { order_id: orderId.trim(), email: email.trim() },
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Lookup failed — try again");
    } finally {
      setLoading(false);
    }
  };

  const activeIndex = result ? STEPS.findIndex((s) => s.key === result.fulfillment) : -1;

  return (
    <div data-testid="track-page" className="min-h-screen bg-neutral-950 text-neutral-100 grid-pattern px-6 py-24">
      <div className="max-w-xl mx-auto space-y-8">
        <div>
          <Link to="/" data-testid="track-back-link" className="text-xs font-mono uppercase tracking-widest text-neutral-400 hover:text-white transition-colors">
            ← Back to the shop
          </Link>
          <h1 className="font-disp text-4xl sm:text-5xl font-extrabold uppercase text-neutral-50 mt-4">Track Your Order</h1>
          <p className="text-sm text-neutral-400 mt-2">Enter your order number and the email you used at checkout.</p>
        </div>

        <form data-testid="track-form" onSubmit={lookup} className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 space-y-4">
          <div>
            <label htmlFor="track-order-id" className="block text-xs font-mono uppercase text-neutral-300 mb-1">Order Number</label>
            <input
              id="track-order-id"
              data-testid="track-order-id-input"
              required
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="e.g. 9d677557-…"
              className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm font-mono text-neutral-100 focus:border-white focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="track-email" className="block text-xs font-mono uppercase text-neutral-300 mb-1">Email</label>
            <input
              id="track-email"
              data-testid="track-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="rider@example.com"
              className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none"
            />
          </div>
          <button
            data-testid="track-submit-button"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-white hover:bg-neutral-200 disabled:opacity-50 text-neutral-950 font-bold uppercase tracking-wider text-sm rounded transition-colors"
          >
            <Search className="w-4 h-4" />
            {loading ? "Looking up…" : "Track Order"}
          </button>
        </form>

        {error && (
          <p data-testid="track-error" className="text-sm text-red-400 font-mono border border-red-500/40 bg-red-500/10 rounded p-4">
            {error}
          </p>
        )}

        {result && (
          <div data-testid="track-result" className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-neutral-800 pb-4">
              <span className="font-mono text-xs text-neutral-400">#{result.order_id.slice(0, 8)}</span>
              <span
                data-testid="track-payment-status"
                className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${
                  result.payment_status === "paid"
                    ? "text-white border-white/40 bg-white/10"
                    : "text-neutral-400 border-neutral-700"
                }`}
              >
                {result.payment_status}
              </span>
            </div>
            <div className="text-sm text-neutral-200">
              {result.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}
            </div>
            <div className="space-y-0">
              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                const reached = idx <= activeIndex;
                return (
                  <div key={step.key} data-testid={`track-step-${step.key}`} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center border ${
                          reached ? "bg-white text-neutral-950 border-white" : "bg-neutral-950 text-neutral-600 border-neutral-800"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      {idx < STEPS.length - 1 && (
                        <div className={`w-px h-10 ${idx < activeIndex ? "bg-white" : "bg-neutral-800"}`} />
                      )}
                    </div>
                    <div className="pb-6">
                      <div className={`text-sm font-bold uppercase tracking-wider ${reached ? "text-neutral-100" : "text-neutral-600"}`}>
                        {step.label}
                      </div>
                      <div className={`text-xs mt-0.5 ${reached ? "text-neutral-400" : "text-neutral-700"}`}>{step.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
